<template>
  <Teleport to="body">
    <div v-if="isOpen" class="modal-backdrop" @click="closeModal">
      <div class="modal-dialog" @click.stop>
        <div class="modal-content">
          <div class="modal-controls">
            <h4 class="modal-title">{{ title }}</h4>
            <button type="button" class="close-btn" @click="closeModal" aria-label="Close">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>

          <div class="modal-body">
            <!-- Mounted only while open, so it auto-loads a fresh QR on open and cleans up on close. -->
            <MobileLoginQRInline @phase-change="phase = $event" />
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import MobileLoginQRInline from './MobileLoginQRInline.vue';

type Phase = 'loading' | 'qr' | 'code' | 'success' | 'expired' | 'error';

const isOpen = ref(false);
// Mirrors the child's phase (emitted) purely so the modal title can react to success.
const phase = ref<Phase>('loading');

const title = computed(() => (phase.value === 'success' ? 'All set' : 'Sign in on your phone'));

function openModal() {
  phase.value = 'loading';
  isOpen.value = true;
  document.body.style.overflow = 'hidden';
}

function closeModal() {
  isOpen.value = false;
  document.body.style.overflow = '';
}

function handleEscape(event: KeyboardEvent) {
  if (event.key === 'Escape' && isOpen.value) closeModal();
}

onMounted(() => {
  document.addEventListener('keydown', handleEscape);
  (window as any).openMobileLoginQRModal = openModal;
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscape);
  delete (window as any).openMobileLoginQRModal;
});
</script>

<style scoped lang="scss">
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.modal-dialog {
  max-width: 480px;
  width: 90%;
}

.modal-content {
  border-radius: 12px;
  color: white;
  background-color: var(--bs-body-bg);
  padding: 1.25rem;
}

.modal-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.modal-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.close-btn {
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
  padding: 0.25rem;
}
</style>
